import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Card,
    CardContent,
    CardActions,
    Button,
    Grid,
    Chip,
    CircularProgress,
    Box,
    Alert,
    Tooltip,
    Stack,
    Avatar,
    Divider,
    Tabs,
    Tab
} from '@mui/material';
import { format } from 'date-fns';
import { Lock, LockOpen, Quiz, AccessTime, Warning, VideoCall, ScreenShare, CalendarToday, Class, Feedback, MenuBook } from '@mui/icons-material';
import Loader from '../component/Loader/Loader';
import { getAllExamOfStudent } from '../helper/student_helper';
import { useNavigate } from 'react-router-dom';

const StudentExams = () => {
    const [exams, setExams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [authChecked, setAuthChecked] = useState(false);
    const navigate = useNavigate();
    const [selectedTab, setSelectedTab] = useState(0);

    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
        switch(newValue) {
            case 0:
                break;
            case 1:
                navigate('/myCourses');
                break;
            case 2:
                navigate('/myFeedbacks');
                break;
            case 3:
                navigate('/live-classes');
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        const checkAuthAndFetchExams = async () => {
            try {
                setLoading(true);
                
                const studentData = localStorage.getItem('student');
                
                if (!studentData) {
                    const currentPath = window.location.pathname + window.location.search;
                    sessionStorage.setItem('returnUrl', currentPath);
                    navigate('/login');
                    return;
                }
                
                const student = JSON.parse(studentData);
                const id = student?.id;
                
                if (!id) {
                    throw new Error('Student ID not found. Please login again.');
                }
                
                const data = await getAllExamOfStudent(id);
                
                if (!data.success) {
                    throw new Error(data.message || 'Failed to fetch exams');
                }
                
                const examsData = (data?.result || []).filter(exam => exam).map(exam => ({
                    id: exam.id,
                    title: exam.title || 'Untitled Exam',
                    description: exam.description || '',
                    questionCount: exam.questionCount || exam.totalQuestions || 0,
                    durationMinutes: exam.durationMinutes || 30,
                    maxWarnings: exam.maxWarnings || 3,
                    lastAttemptDate: exam.lastAttemptDate,
                    status: exam.status || (exam.isAccessible ? 'unlocked' : 'locked'),
                    isAccessible: exam.isAccessible || exam.status === 'unlocked' || false,
                    saveLiveVideo: exam.saveLiveVideo || false,
                    saveScreenShare: exam.saveScreenShare || false,
                    isLocked: exam.status === 'locked' || !(exam.isAccessible || exam.status === 'unlocked')
                }));
                
                console.log('Processed exams from backend:', examsData);
                setExams(examsData);
                setError(null);
                setAuthChecked(true);
                
            } catch (err) {
                console.error('Error fetching exams:', err);
                setError(err.message || 'An error occurred while fetching exams');
                
                if (err.message.includes('Student ID') || 
                    err.message.includes('unauthorized') || 
                    err.message.includes('Unauthorized') ||
                    err.response?.status === 401) {
                    
                    localStorage.removeItem('student');
                    localStorage.removeItem('token');
                    
                    const returnUrl = window.location.pathname + window.location.search;
                    sessionStorage.setItem('returnUrl', returnUrl);
                    navigate('/login');
                }
                
            } finally {
                setLoading(false);
            }
        };

        checkAuthAndFetchExams();
    }, [navigate]);

    const parseBackendDate = (dateArray) => {
        if (!dateArray) return new Date();
        
        if (Array.isArray(dateArray)) {
            return new Date(
                dateArray[0],
                dateArray[1] - 1,
                dateArray[2] || 1,
                dateArray[3] || 0,
                dateArray[4] || 0
            );
        }
        
        const parsedDate = new Date(dateArray);
        return isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
    };

    const handleStartExam = (exam) => {
        if (exam.status === 'locked' || exam.isLocked) {
            alert('This exam is locked. Please contact your instructor to unlock it.');
            return;
        }
        
        if (isExamExpired(exam)) {
            alert('This exam has expired and cannot be attempted.');
            return;
        }
        
        const studentData = localStorage.getItem('student');
        if (!studentData) {
            const examPath = `/exam/${exam.id}/attempt`;
            sessionStorage.setItem('returnUrl', examPath);
            navigate('/login');
            return;
        }
        
        if (exam.saveLiveVideo || exam.saveScreenShare) {
            const confirmMessage = exam.saveLiveVideo && exam.saveScreenShare 
                ? 'This exam will record both your video and screen. Do you want to continue?'
                : exam.saveLiveVideo 
                ? 'This exam will record your video. Do you want to continue?'
                : 'This exam will record your screen. Do you want to continue?';
            
            if (!window.confirm(confirmMessage)) {
                return;
            }
        }
        
        navigate(`/exam/${exam.id}/attempt`);
    };

    const handleViewDetails = (exam) => {
        navigate(`/exam/${exam.id}/details`);
    };

    const handleLogout = () => {
        localStorage.removeItem('student');
        localStorage.removeItem('token');
        navigate('/login');
    };

    const isExamExpired = (exam) => {
        if (!exam.lastAttemptDate) return false;
        
        try {
            const lastAttemptDate = parseBackendDate(exam.lastAttemptDate);
            const now = new Date();
            return lastAttemptDate < now;
        } catch (error) {
            console.error('Error parsing date:', error);
            return false;
        }
    };

    const canAttemptExam = (exam) => {
        const isLocked = exam.status === 'locked' || exam.isLocked;
        const isExpired = isExamExpired(exam);
        return !isLocked && !isExpired;
    };

    const availableExams = exams.filter(exam => canAttemptExam(exam));
    const lockedExams = exams.filter(exam => exam.status === 'locked' || exam.isLocked);
    const expiredExams = exams.filter(exam => exam.status !== 'locked' && !exam.isLocked && isExamExpired(exam));

    const formatDisplayDate = (dateArray) => {
        try {
            const date = parseBackendDate(dateArray);
            return format(date, 'MMM dd, yyyy HH:mm');
        } catch (error) {
            return 'Date not specified';
        }
    };

    const getStatusConfig = (exam) => {
        if (exam.status === 'locked' || exam.isLocked) {
            return {
                label: 'Locked',
                color: 'error',
                icon: <Lock fontSize="small" />,
                tooltip: 'Students cannot access this exam'
            };
        }
        
        if (isExamExpired(exam)) {
            return {
                label: 'Expired',
                color: 'warning',
                icon: <CalendarToday fontSize="small" />,
                tooltip: 'Exam period has ended'
            };
        }
        
        return {
            label: 'Unlocked',
            color: 'success',
            icon: <LockOpen fontSize="small" />,
            tooltip: 'Students can access this exam'
        };
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" flexDirection="column">
                    <CircularProgress size={60} />
                    <Typography variant="body1" sx={{ mt: 3 }}>
                        Loading your exams...
                    </Typography>
                </Box>
            </Container>
        );
    }

    if (!authChecked && !loading && !localStorage.getItem('student')) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box textAlign="center" mt={6}>
                    <Alert severity="info" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
                        Please login to view your exams
                    </Alert>
                    <Button 
                        variant="contained" 
                        onClick={() => {
                            const returnUrl = window.location.pathname + window.location.search;
                            sessionStorage.setItem('returnUrl', returnUrl);
                            navigate('/login');
                        }}
                        sx={{ mt: 2 }}
                    >
                        Go to Login
                    </Button>
                </Box>
            </Container>
        );
    }

    if (error && !exams.length) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box textAlign="center" mt={6}>
                    <Typography variant="h6" color="error" gutterBottom>
                        Error Loading Exams
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph sx={{ maxWidth: 600, mx: 'auto' }}>
                        {error}
                    </Typography>
                    <Button 
                        variant="contained" 
                        onClick={() => window.location.reload()}
                        sx={{ mt: 2 }}
                    >
                        Retry
                    </Button>
                    <Button 
                        variant="outlined" 
                        onClick={handleLogout}
                        sx={{ mt: 2, ml: 2 }}
                    >
                        Logout & Login Again
                    </Button>
                </Box>
            </Container>
        );
    }

    const studentData = JSON.parse(localStorage.getItem('student') || '{}');
    const studentName = studentData?.name || studentData?.email || 'Student';

    const renderExamCard = (exam, isBlurred = false) => {
        const statusConfig = getStatusConfig(exam);
        const isExpired = isExamExpired(exam);
        const isLocked = exam.status === 'locked' || exam.isLocked;
        
        return (
            <Card 
                sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    border: '2px solid',
                    borderColor: isLocked ? 'error.light' : isExpired ? 'warning.light' : 'success.light',
                    filter: isLocked ? 'blur(3px)' : 'none',
                    opacity: isLocked ? 0.6 : isExpired ? 0.9 : 1,
                    position: 'relative',
                    cursor: isLocked ? 'not-allowed' : 'pointer',
                    '&:hover': !isLocked && {
                        transform: 'translateY(-4px)',
                        boxShadow: 6,
                        borderColor: isExpired ? 'warning.main' : 'success.main'
                    }
                }}
            >
                {isLocked && (
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 2,
                        textAlign: 'center',
                        backgroundColor: 'rgba(255,255,255,0.95)',
                        borderRadius: 3,
                        p: 3,
                        width: '85%',
                        boxShadow: 3,
                        border: '2px solid',
                        borderColor: 'error.main'
                    }}>
                        <Lock color="error" sx={{ fontSize: 50, mb: 2 }} />
                        <Typography variant="h6" color="error.main" gutterBottom fontWeight="bold">
                            Exam Locked
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Students cannot access this exam
                        </Typography>
                        <Chip 
                            label="Contact Instructor"
                            color="error"
                            variant="outlined"
                        />
                    </Box>
                )}

                <CardContent sx={{ flexGrow: 1, position: 'relative', zIndex: isLocked ? 0 : 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <Avatar sx={{ 
                            bgcolor: isLocked ? 'error.main' : isExpired ? 'warning.main' : 'success.main', 
                            mr: 2,
                            opacity: isLocked ? 0.7 : 1
                        }}>
                            <Quiz />
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                            <Typography gutterBottom variant="h5" component="h2" noWrap>
                                {exam.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {exam.questionCount} Questions
                            </Typography>
                        </Box>
                    </Box>
                    
                    <Tooltip title={statusConfig.tooltip}>
                        <Chip
                            icon={statusConfig.icon}
                            label={statusConfig.label}
                            size="small"
                            color={statusConfig.color}
                            sx={{ mr: 1, mb: 2, opacity: isLocked ? 0.7 : 1 }}
                        />
                    </Tooltip>

                    <Stack spacing={1} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', opacity: isLocked ? 0.7 : 1 }}>
                            <AccessTime fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                            <Typography variant="body2">
                                {exam.durationMinutes} minutes
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', opacity: isLocked ? 0.7 : 1 }}>
                            <Warning fontSize="small" sx={{ mr: 1, color: 'warning.main' }} />
                            <Typography variant="body2">
                                {exam.maxWarnings} warnings allowed
                            </Typography>
                        </Box>
                    </Stack>

                    {(exam.saveLiveVideo || exam.saveScreenShare) && (
                        <Box sx={{ mb: 2, opacity: isLocked ? 0.7 : 1 }}>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="caption" color="text.secondary" gutterBottom>
                                Proctoring:
                            </Typography>
                            <Stack direction="row" spacing={1}>
                                {exam.saveLiveVideo && (
                                    <Chip
                                        icon={<VideoCall fontSize="small" />}
                                        label="Video"
                                        size="small"
                                        color="error"
                                        variant="outlined"
                                    />
                                )}
                                {exam.saveScreenShare && (
                                    <Chip
                                        icon={<ScreenShare fontSize="small" />}
                                        label="Screen"
                                        size="small"
                                        color="success"
                                        variant="outlined"
                                    />
                                )}
                            </Stack>
                        </Box>
                    )}

                    {exam.lastAttemptDate && (
                        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider', opacity: isLocked ? 0.7 : 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                <strong>Last Attempt:</strong> {formatDisplayDate(exam.lastAttemptDate)}
                            </Typography>
                            {isExpired && !isLocked && (
                                <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
                                    <Typography variant="body2">
                                        Exam period has ended
                                    </Typography>
                                </Alert>
                            )}
                        </Box>
                    )}
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between', p: 2, pt: 0, position: 'relative', zIndex: isLocked ? 0 : 1 }}>
                    <Tooltip title={isLocked ? "Cannot view details of locked exam" : "View exam details"}>
                        <span>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleViewDetails(exam)}
                                disabled={isLocked}
                                sx={{ opacity: isLocked ? 0.7 : 1 }}
                            >
                                View Details
                            </Button>
                        </span>
                    </Tooltip>
                    
                    <Tooltip title={isLocked ? "This exam is locked. Contact your instructor to unlock it." : 
                                   isExpired ? "Exam period has ended. Cannot attempt now." : 
                                   "Start the exam"}>
                        <span>
                            <Button
                                size="small"
                                variant="contained"
                                color={isLocked ? 'error' : isExpired ? 'warning' : 'success'}
                                onClick={() => handleStartExam(exam)}
                                disabled={isLocked || isExpired}
                                sx={{ 
                                    minWidth: '120px',
                                    opacity: (isLocked || isExpired) ? 0.7 : 1
                                }}
                            >
                                {isLocked ? 'Locked' : isExpired ? 'Expired' : 'Start Exam'}
                            </Button>
                        </span>
                    </Tooltip>
                </CardActions>
            </Card>
        );
    };

    return (
        <>
            {loading ? <Loader /> : null}

            <Container maxWidth="lg" sx={{ py: 4 }}>
                {/* Tab Navigation */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
                    <Tabs 
                        value={selectedTab} 
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                    >
                        <Tab 
                            icon={<Quiz />}
                            label="MY EXAM" 
                            iconPosition="start"
                        />
                        <Tab 
                            icon={<MenuBook />}
                            label="MY COURSE" 
                            iconPosition="start"
                        />
                        <Tab 
                            icon={<Feedback />}
                            label="MY FEEDBACK" 
                            iconPosition="start"
                        />
                        <Tab 
                            icon={<Class />}
                            label="LIVE CLASSES" 
                            iconPosition="start"
                        />
                    </Tabs>
                </Box>

                {/* Header Section */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
                        My Exams Dashboard
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                        Welcome back, {studentName}! Here are all your assigned exams.
                    </Typography>
                    
                    <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                        <Chip 
                            icon={<Quiz />}
                            label={`Total: ${exams.length}`}
                            color="primary"
                            variant="outlined"
                        />
                        <Chip 
                            icon={<LockOpen />}
                            label={`Available: ${availableExams.length}`}
                            color="success"
                            variant="outlined"
                        />
                        <Chip 
                            icon={<Lock />}
                            label={`Locked: ${lockedExams.length}`}
                            color="error"
                            variant="outlined"
                        />
                        <Chip 
                            icon={<CalendarToday />}
                            label={`Expired: ${expiredExams.length}`}
                            color="warning"
                            variant="outlined"
                        />
                    </Stack>

                    <Box sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: 2, 
                        mb: 3,
                        p: 2,
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        border: 1,
                        borderColor: 'divider'
                    }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="bold">
                            Legend:
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 12, height: 12, bgcolor: 'success.main', borderRadius: '50%' }} />
                            <Typography variant="caption">Available & Unlocked</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 12, height: 12, bgcolor: 'error.main', borderRadius: '50%' }} />
                            <Typography variant="caption">Locked (Cannot Access)</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 12, height: 12, bgcolor: 'warning.main', borderRadius: '50%' }} />
                            <Typography variant="caption">Expired (Past Date)</Typography>
                        </Box>
                    </Box>
                </Box>

                {exams.length === 0 ? (
                    <Box textAlign="center" mt={6} sx={{ py: 8, bgcolor: 'background.paper', borderRadius: 2 }}>
                        <Quiz sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            No exams available
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 3 }}>
                            You don't have any exams assigned yet. Please check back later or contact your instructor.
                        </Typography>
                        <Button 
                            variant="contained" 
                            onClick={() => window.location.reload()}
                            sx={{ mt: 2 }}
                        >
                            Refresh Exams
                        </Button>
                    </Box>
                ) : (
                    <>
                        {availableExams.length > 0 && (
                            <Box sx={{ mb: 5 }}>
                                <Typography variant="h5" gutterBottom sx={{ mb: 3, color: 'success.main', display: 'flex', alignItems: 'center' }}>
                                    <LockOpen sx={{ mr: 1 }} />
                                    Available Exams ({availableExams.length})
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                                        (Students can access these exams)
                                    </Typography>
                                </Typography>
                                <Grid container spacing={3}>
                                    {availableExams.map((exam) => (
                                        <Grid item xs={12} sm={6} md={4} key={exam.id}>
                                            {renderExamCard(exam, false)}
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        )}

                        {lockedExams.length > 0 && (
                            <Box sx={{ mb: 5 }}>
                                <Typography variant="h5" gutterBottom sx={{ mb: 3, color: 'error.main', display: 'flex', alignItems: 'center' }}>
                                    <Lock sx={{ mr: 1 }} />
                                    Locked Exams ({lockedExams.length})
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                                        (Students cannot access these exams)
                                    </Typography>
                                </Typography>
                                <Alert severity="error" sx={{ mb: 3 }}>
                                    <Typography variant="body2">
                                        These exams are locked by your instructor. Please contact them to unlock the exams.
                                    </Typography>
                                </Alert>
                                <Grid container spacing={3}>
                                    {lockedExams.map((exam) => (
                                        <Grid item xs={12} sm={6} md={4} key={exam.id}>
                                            {renderExamCard(exam, true)}
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        )}

                        {expiredExams.length > 0 && (
                            <Box sx={{ mb: 5 }}>
                                <Typography variant="h5" gutterBottom sx={{ mb: 3, color: 'warning.main', display: 'flex', alignItems: 'center' }}>
                                    <CalendarToday sx={{ mr: 1 }} />
                                    Expired Exams ({expiredExams.length})
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                                        (Past last attempt date)
                                    </Typography>
                                </Typography>
                                <Alert severity="warning" sx={{ mb: 3 }}>
                                    <Typography variant="body2">
                                        These exams have passed their last attempt date and cannot be attempted.
                                    </Typography>
                                </Alert>
                                <Grid container spacing={3}>
                                    {expiredExams.map((exam) => (
                                        <Grid item xs={12} sm={6} md={4} key={exam.id}>
                                            {renderExamCard(exam, false)}
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        )}
                    </>
                )}
                
                <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary">
                                <strong>Quick Links:</strong>
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                                <Button size="small" onClick={() => navigate('/myCourses')}>My Courses</Button>
                                <Button size="small" onClick={() => navigate('/live-classes')}>Live Classes</Button>
                                <Button size="small" onClick={() => navigate('/myFeedbacks')}>MyFeedback</Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>                   
            </Container>
        </>
    );
};

export default StudentExams;
